import { useAssistant, type ConversationSummary } from "~/composables/useAssistant";

/**
 * Holds the assistant's saved conversations (the sessions rail) and which one is active. The transcript
 * itself and the live streaming state live in `AppQBotSearch.vue`; this store only owns the list + the
 * active id so they survive the palette closing/reopening.
 */
export const useAssistantStore = defineStore("Assistant", () => {
	const { getConversations, createConversation, deleteConversation } = useAssistant();

	const {
		state: conversations,
		execute: refresh,
		isLoading,
	} = useAsyncState(getConversations, [], {
		immediate: false,
		resetOnExecute: false,
	});

	// null = a fresh, not-yet-persisted chat (created on first question).
	const activeConversationId = ref<string | null>(null);

	function newChat() {
		activeConversationId.value = null;
	}

	function select(id: string) {
		activeConversationId.value = id;
	}

	/** Persist a new conversation (title = the first question), prepend it, and make it active. */
	async function start(title: string): Promise<string> {
		const created = await createConversation(title);
		conversations.value = [created, ...conversations.value];
		activeConversationId.value = created.id;
		return created.id;
	}

	async function remove(id: string) {
		await deleteConversation(id);
		conversations.value = conversations.value.filter((c: ConversationSummary) => c.id !== id);
		if (activeConversationId.value === id) activeConversationId.value = null;
	}

	return {
		conversations,
		activeConversationId,
		isLoading,
		refresh: () => refresh(),
		newChat,
		select,
		start,
		remove,
	};
});
